import { type Config } from "tailwindcss"
import colors from "tailwindcss/colors"

export const extendedTheme = {
	colors: {
		"colors-cerise": "var(--colors-cerise)",
		"colors-charcoal": "var(--colors-charcoal)",
		"colors-cobalt": "var(--colors-cobalt)",
		"colors-electric": "var(--colors-electric)",
		"colors-gray-900": "var(--colors-gray-900)",
		"colors-maya": "var(--colors-maya)",
		"colors-medallion": "var(--colors-medallion)",
		"colors-monarch": "var(--colors-monarch)",
		"colors-sheen": "var(--colors-sheen)",
		"colors-smoke": "var(--colors-smoke)",
		"colors-steel": "var(--colors-steel)",
		"neutral-10": "var(--neutral-10)",
		"journey-turquoise": "#50C4C5",
		fontFamily: {
			"body-small": "var(--body-small-font-family)",
		},
		border: "hsl(var(--border))",
		input: {
			DEFAULT: "hsl(var(--input))",
			invalid: "hsl(var(--input-invalid))",
		},
		ring: {
			DEFAULT: "hsl(var(--ring))",
			invalid: "hsl(var(--foreground-destructive))",
		},
		background: "hsl(var(--background))",
		foreground: {
			DEFAULT: "hsl(var(--foreground))",
			destructive: "hsl(var(--foreground-destructive))",
		},
		primary: {
			DEFAULT: "hsl(var(--primary))",
			foreground: "hsl(var(--primary-foreground))",
		},
		secondary: {
			DEFAULT: "hsl(var(--secondary))",
			foreground: "hsl(var(--secondary-foreground))",
		},
		destructive: {
			DEFAULT: "hsl(var(--destructive))",
			foreground: "hsl(var(--destructive-foreground))",
		},
		muted: {
			DEFAULT: "hsl(var(--muted))",
			foreground: "hsl(var(--muted-foreground))",
		},
		accent: {
			DEFAULT: "hsl(var(--accent))",
			foreground: "hsl(var(--accent-foreground))",
		},
		popover: {
			DEFAULT: "hsl(var(--popover))",
			foreground: "hsl(var(--popover-foreground))",
		},
		card: {
			DEFAULT: "hsl(var(--card))",
			foreground: "hsl(var(--card-foreground))",
		},
		// light mode
		tremor: {
			brand: {
				faint: colors.blue[50],
				muted: colors.blue[200],
				subtle: colors.blue[400],
				DEFAULT: colors.blue[500],
				emphasis: colors.blue[700],
				inverted: colors.white,
			},
			background: {
				muted: colors.gray[50],
				subtle: colors.gray[100],
				DEFAULT: colors.white,
				emphasis: colors.gray[700],
			},
			border: {
				DEFAULT: colors.gray[200],
			},
			ring: {
				DEFAULT: colors.gray[200],
			},
			content: {
				subtle: colors.gray[400],
				DEFAULT: colors.gray[500],
				emphasis: colors.gray[700],
				strong: colors.gray[900],
				inverted: colors.white,
			},
		},
		// dark mode
		"dark-tremor": {
			brand: {
				faint: "#0B1229",
				muted: colors.blue[950],
				subtle: colors.blue[800],
				DEFAULT: colors.blue[500],
				emphasis: colors.blue[400],
				inverted: colors.blue[950],
			},
			background: {
				muted: "#131A2B",
				subtle: colors.gray[800],
				DEFAULT: colors.gray[900],
				emphasis: colors.gray[300],
			},
			border: {
				DEFAULT: colors.gray[800],
			},
			ring: {
				DEFAULT: colors.gray[800],
			},
			content: {
				subtle: colors.gray[600],
				DEFAULT: colors.gray[500],
				emphasis: colors.gray[200],
				strong: colors.gray[50],
				inverted: colors.gray[950],
			},
		},
	},
	borderColor: {
		DEFAULT: "hsl(var(--border))",
		"tremor-small": "0.375rem",
		"tremor-default": "0.5rem",
		"tremor-full": "9999px",
	},
	borderRadius: {
		lg: "var(--radius)",
		md: "calc(var(--radius) - 2px)",
		sm: "calc(var(--radius) - 4px)",
	},
	fontSize: {
		// 1rem = 16px
		/** 80px size / 84px high / bold */
		mega: ["5rem", { lineHeight: "5.25rem", fontWeight: "700" }],
		/** 56px size / 62px high / bold */
		h1: ["3.5rem", { lineHeight: "3.875rem", fontWeight: "700" }],
		/** 40px size / 48px high / bold */
		h2: ["2.5rem", { lineHeight: "3rem", fontWeight: "700" }],
		/** 32px size / 36px high / bold */
		h3: ["2rem", { lineHeight: "2.25rem", fontWeight: "700" }],
		/** 28px size / 36px high / bold */
		h4: ["1.75rem", { lineHeight: "2.25rem", fontWeight: "700" }],
		/** 24px size / 32px high / bold */
		h5: ["1.5rem", { lineHeight: "2rem", fontWeight: "700" }],
		/** 16px size / 20px high / bold */
		h6: ["1rem", { lineHeight: "1.25rem", fontWeight: "700" }],

		/** 32px size / 36px high / normal */
		"body-2xl": ["2rem", { lineHeight: "2.25rem" }],
		/** 28px size / 36px high / normal */
		"body-xl": ["1.75rem", { lineHeight: "2.25rem" }],
		/** 24px size / 32px high / normal */
		"body-lg": ["1.5rem", { lineHeight: "2rem" }],
		/** 20px size / 28px high / normal */
		"body-md": ["1.25rem", { lineHeight: "1.75rem" }],
		/** 16px size / 20px high / normal */
		"body-sm": ["1rem", { lineHeight: "1.25rem" }],
		/** 14px size / 18px high / normal */
		"body-xs": ["0.875rem", { lineHeight: "1.125rem" }],
		/** 12px size / 16px high / normal */
		"body-2xs": ["0.75rem", { lineHeight: "1rem" }],

		/** 18px size / 24px high / semibold */
		caption: ["1.125rem", { lineHeight: "1.5rem", fontWeight: "600" }],
		/** 12px size / 16px high / bold */
		button: ["0.75rem", { lineHeight: "1rem", fontWeight: "700" }],

		"tremor-label": ["0.75rem", { lineHeight: "1rem" }],
		"tremor-default": ["0.875rem", { lineHeight: "1.25rem" }],
		"tremor-title": ["1.125rem", { lineHeight: "1.75rem" }],
		"tremor-metric": ["1.875rem", { lineHeight: "2.25rem" }],
	},
	backgroundImage: {
		"experience-profile-bg": 'url("/img/experience_profile_bg.webp")',
		"rainfocus-bg": 'url("/img/rainfocus_bg.png")',
	},
	width: {
		"92%": "92%",
		"85%": "85%",
		"78%": "78%",
		"71%": "71%",
		"64%": "64%",
		"57%": "57%",
		"42%": "42%",
		"35%": "35%",
		"28%": "28%",
		"21%": "21%",
		"14%": "14%",
		"7%": "7%",
	},
	boxShadow: {
		// light
		"tremor-input": "0 1px 2px 0 rgb(0 0 0 / 0.05)",
		"tremor-card":
			"0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
		"tremor-dropdown":
			"0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
		// dark
		"dark-tremor-input": "0 1px 2px 0 rgb(0 0 0 / 0.05)",
		"dark-tremor-card":
			"0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
		"dark-tremor-dropdown":
			"0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
	},
} satisfies Config["theme"]
